<template>
  <header class="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-700">
    <nav class="container mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <!-- Logo -->
        <router-link to="/" class="flex items-center space-x-2">
          <div class="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
            <span class="text-white font-bold text-lg">K</span>
          </div>
          <!-- <span class="text-xl font-bold text-gray-900 dark:text-white">
            {{ $t('hero.name') }}
          </span> -->
        </router-link>

        <!-- Desktop Navigation -->
        <div class="hidden md:flex items-center space-x-2">
          <router-link
            v-for="item in navItems"
            :key="item.name"
            :to="item.to"
            class="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 font-medium"
            :class="{ 
              'text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800': $route.path === item.to 
            }"
          >
            {{ $t(item.label) }}
          </router-link>
        </div>

        <!-- Theme Toggle & Language Switcher -->
        <div class="flex items-center space-x-4">
          <!-- Language Switcher -->
          <div class="relative">
            <button
              @click="toggleLanguageDropdown"
              class="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
            >
              <img 
                :src="currentLanguage === 'en' ? require('@/assets/flags/en.png') : require('@/assets/flags/km.png')" 
                :alt="currentLanguage === 'en' ? 'English' : 'Khmer'"
                class="w-6 h-6 object-cover rounded-sm"
              />
              <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
                {{ currentLanguage.toUpperCase() }}
              </span>
              <ChevronDownIcon class="w-0 h-0 text-white" />
            </button>
            
            <!-- Language Dropdown -->
            <div
              v-if="showLanguageDropdown"
              class="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700"
            >
              <button
                @click="setLanguage('en')"
                class="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg flex items-center space-x-2"
                :class="{ 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400': currentLanguage === 'en' }"
              >
                <img src="@/assets/flags/en.png" alt="English" class="w-4 h-4 object-cover rounded-sm" />
                <span>English</span>
              </button>
              <button
                @click="setLanguage('km')"
                class="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg khmer flex items-center space-x-2"
                :class="{ 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400': currentLanguage === 'km' }"
              >
                <img src="@/assets/flags/km.png" alt="Khmer" class="w-4 h-4 object-cover rounded-sm" />
                <span>ខ្មែរ</span>
              </button>
            </div>
          </div>

          <!-- Theme Toggle -->
          <button
            @click="toggleTheme"
            class="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
            :title="isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'"
          >
            <SunIcon v-if="isDarkMode" class="w-5 h-5 text-yellow-500" />
            <MoonIcon v-else class="w-5 h-5 text-gray-600" />
          </button>

          <!-- Mobile Menu Button -->
          <button
            @click="toggleMobileMenu"
            class="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
          >
            <Bars3Icon v-if="!isMenuOpen" class="w-6 h-6 text-gray-600 dark:text-gray-300" />
            <XMarkIcon v-else class="w-6 h-6 text-gray-600 dark:text-gray-300" />
          </button>
        </div>
      </div>

      <!-- Mobile Navigation -->
      <div
        v-if="isMenuOpen"
        class="md:hidden py-4 border-t border-gray-200 dark:border-gray-700"
      >
        <div class="flex flex-col space-y-2">
          <router-link
            v-for="item in navItems"
            :key="item.name"
            :to="item.to"
            @click="closeMobileMenu"
            class="px-4 py-3 mx-2 rounded-lg text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 font-medium"
            :class="{ 
              'text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800': $route.path === item.to 
            }"
          >
            {{ $t(item.label) }}
          </router-link>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import {
  SunIcon,
  MoonIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon
} from '@heroicons/vue/24/outline'

const store = useStore()
const { locale } = useI18n()

const showLanguageDropdown = ref(false)

const navItems = [
  { name: 'home', label: 'nav.home', to: '/' },
  { name: 'about', label: 'nav.about', to: '/#about' },
  { name: 'skills', label: 'nav.skills', to: '/#skills' },
  { name: 'projects', label: 'nav.projects', to: '/#projects' },
  { name: 'experience', label: 'nav.experience', to: '/#experience' },
  { name: 'contact', label: 'nav.contact', to: '/#contact' }
]

const isDarkMode = computed(() => store.getters.isDarkMode)
const currentLanguage = computed(() => store.getters.currentLanguage)
const isMenuOpen = computed(() => store.getters.isMenuOpen)

const toggleTheme = () => {
  store.dispatch('toggleTheme')
}

const toggleLanguageDropdown = () => {
  showLanguageDropdown.value = !showLanguageDropdown.value
}

const setLanguage = (lang) => {
  store.dispatch('setLanguage', lang)
  locale.value = lang
  showLanguageDropdown.value = false
}

const toggleMobileMenu = () => {
  store.dispatch('toggleMenu')
}

const closeMobileMenu = () => {
  store.dispatch('setMenuOpen', false)
}

const closeDropdowns = (event) => {
  if (!event.target.closest('.relative')) {
    showLanguageDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', closeDropdowns)
})

onUnmounted(() => {
  document.removeEventListener('click', closeDropdowns)
})
</script> 